<script setup>
    import { ref, watch, computed } from 'vue'
    import { useChannelsStore } from '@/stores/channels'
    import axios from 'axios'
    import ColorInput from 'vue-color-input'
    import MultiSelect from 'primevue/multiselect'
    import ToolbarFile from '@/components/library/ToolbarFile.vue'
    import InputFormHorizontal from '@/components/UI/Form/InputFormHorizontal.vue'
    import InputCheckBoxWithDescription from '@/components/UI/Form/InputCheckBoxWithDescription.vue'
    import DivDivider from '@/components/utils/DivDivider.vue'
    import AlertPrice from '@/views/dash/channels/Pages/AlertPrice.vue'
    import FeedbackAlert from '@/components/utils/FeedbackAlert.vue'
    import Dialog from 'primevue/dialog'
    import { getUrlBaseV2, copyToClipboard, removeAccent } from '@/utils/index'

    function normalizeSlug(value) {
        return removeAccent(String(value ?? '')).toLowerCase()
    }

    const store = useChannelsStore()
    const channel = store.channel

    const loginTypes = [
        { label: 'Documento', value: 'document' },
        { label: 'Nenhum', value: 'none' },
    ]

    const countryOptions = [
        { label: 'Brasil', value: 'BR' },
        { label: 'Paraguai', value: 'PY' },
        { label: 'Estados Unidos', value: 'EUA' },
    ]

    const additionalFieldsOptions = [
        { label: 'Celular', value: 'phone' },
    ]

    const suggestionRolesOptions = [
        { label: 'Visitante', value: 'visitor' },
        { label: 'Cliente', value: 'client' },
    ]

    if (!store.channel.more) {
        store.channel.more = {}
    }

    store.channel.more = {
        slug: normalizeSlug(channel.more?.slug || ''),
        theme: {
            logo_url: channel.more?.theme?.logo_url || null,
            login_title: channel.more?.theme?.login_title || '',
            login_description: channel.more?.theme?.login_description || '',
            login_visitor_text: channel.more?.theme?.login_visitor_text || 'Entrar como visitante',
            primary_color: channel.more?.theme?.primary_color || '#F97316',
            secondary_color: channel.more?.theme?.secondary_color || '#EA580C',
        },
        login: {
            auth_check: channel.more?.login?.auth_check ?? false,
            type: channel.more?.login?.type || 'document',
            additional_fields: channel.more?.login?.additional_fields || ['phone'],
            country: channel.more?.login?.country || 'BR',
        },
        suggestions: channel.more?.suggestions || [
            { trigger: '', description: '', roles: [] },
        ],
        features: {
            reboot_device: channel.more?.features?.reboot_device ?? false,
            change_wifi: channel.more?.features?.change_wifi ?? false,
            payment_promise: channel.more?.features?.payment_promise ?? false,
            duplicates: channel.more?.features?.duplicates ?? false,
            duplicates_options: {
                omit_pix: channel.more?.features?.duplicates_options?.omit_pix ?? false,
                omit_barcode: channel.more?.features?.duplicates_options?.omit_barcode ?? false,
            },
        },
        notification_token: channel.more?.notification_token || generateNotificationToken(),
    }

    store.channel.type = store.channel.type || 'webchat_v2'
    store.channel.server_type = store.channel.server_type || 'webchat_v2'
    store.channel.user = store.channel.user || Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)

    const isReseller = import.meta.env.VITE_APP_RESELLER === 'true'
    const slugDomain = computed(() => isReseller ? '.web.sendmessages.com.br' : '.web.vmix.chat')
    const apiDomain = computed(() => isReseller ? 'https://web.api.sendmessages.com.br' : 'https://web.api.vmix.chat')
    const webDomain = computed(() => isReseller ? 'https://web.sendmessages.com.br' : 'https://web.vmix.chat')

    const slugStatus = ref(null)
    const slugMessage = ref('')
    const slugChecking = ref(false)
    let slugDebounce = null

    watch(() => store.channel.more.slug, (newSlug) => {
        const normalized = normalizeSlug(newSlug)
        if (newSlug !== normalized) {
            store.channel.more.slug = normalized
            return
        }

        clearTimeout(slugDebounce)
        slugStatus.value = null
        slugMessage.value = ''
        if (!newSlug || String(newSlug).trim().length < 3) return

        slugDebounce = setTimeout(() => {
            verifySlug(String(newSlug).trim())
        }, 600)
    })

    function verifySlug(slug) {
        slugChecking.value = true
        axios.get(`${getUrlBaseV2()}/webchat/verify-slug/${slug}`)
            .then((res) => {
                const data = res.data?.data ?? res.data
                slugStatus.value = data?.available ? 'available' : 'unavailable'
                slugMessage.value = data?.message ?? data?.msg ?? data?.error ?? ''
            })
            .catch((err) => {
                slugStatus.value = 'error'
                slugMessage.value = err?.response?.data?.message ?? err?.response?.data?.error ?? ''
            })
            .finally(() => {
                slugChecking.value = false
            })
    }

    function getSlugFeedback() {
        if (slugChecking.value) return { type: 'info', text: 'Verificando disponibilidade...' }
        if (slugStatus.value === 'available') return { type: 'success', text: 'Slug disponível!' }
        if (slugStatus.value === 'unavailable') return { type: 'error', text: slugMessage.value || 'Este slug já está em uso.' }
        if (slugStatus.value === 'error') return { type: 'error', text: slugMessage.value || 'Erro ao verificar slug.' }
        return null
    }

    function handleLogoSelected(item) {
        store.channel.more.theme.logo_url = item?.source ?? ''
    }

    function handleAuthCheckChange(value) {
        store.channel.more.login.auth_check = !!value
    }

    function addSuggestion() {
        if (!store.channel.more.suggestions) {
            store.channel.more.suggestions = []
        }
        store.channel.more.suggestions.push({ trigger: '', description: '', roles: [] })
    }

    function removeSuggestion(index) {
        if (store.channel.more.suggestions.length > 1) {
            store.channel.more.suggestions.splice(index, 1)
        }
    }

    const additionalFields = computed({
        get() {
            return store.channel.more?.login?.additional_fields || []
        },
        set(val) {
            if (!store.channel.more) store.channel.more = {}
            if (!store.channel.more.login) store.channel.more.login = {}
            store.channel.more.login.additional_fields = val
        },
    })

    const showNotificationModal = ref(false)

    const channelIdForDoc = computed(() => store.channel.id || '{channel_id}')

    const curlCommand = computed(() => {
        return `curl -X POST "${apiDomain.value}/api/channels/${channelIdForDoc.value}/notifications" \\
  -H "Content-Type: application/json" \\
  -H "X-Channel-Token: ${store.channel.more.notification_token}" \\
  -d '{
    "document": "09050825000195",
    "title": "Nova mensagem",
    "content": "Você recebeu uma resposta no atendimento",
    "url": "${webDomain.value}/..."
  }'`
    })

    function generateNotificationToken() {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
        const length = 64
        let token = ''

        const getRandom = () => {
            if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
                const values = new Uint32Array(1)
                crypto.getRandomValues(values)
                return values[0]
            }
            return Math.floor(Math.random() * 0x100000000)
        }

        for (let i = 0; i < length; i++) {
            token += chars[getRandom() % chars.length]
        }

        return token
    }

    function rotateNotificationToken() {
        store.channel.more.notification_token = generateNotificationToken()
    }

    function copyNotificationToken() {
        copyToClipboard(store.channel.more.notification_token)
    }

    function copyCurl() {
        copyToClipboard(curlCommand.value)
    }
</script>

<template>
    <template v-if="store.formStep === 2">
        <div class="space-y-4">
            <FeedbackAlert
                title="Webchat V2"
                content="Configure os detalhes de autenticação e tema do seu novo canal de atendimento via web."
                type="info"
            />

            <InputFormHorizontal
                v-model="store.channel.name"
                :init-value="store.channel.name"
                label="Nome do webchat"
                placeholder="Como identificará seu canal"
                rules="required"
                vertical
            />

            <div class="space-y-1">
                <InputFormHorizontal
                    v-model="store.channel.more.slug"
                    :init-value="store.channel.more.slug"
                    label="URL"
                    placeholder="Identificador único para acesso ao webchat"
                    rules="required"
                    :label-right="slugDomain"
                    label-left="https://"
                    vertical
                />
                <div v-if="getSlugFeedback()" class="flex items-center gap-2 px-1">
                    <span
                        class="text-xs"
                        :class="{
                            'text-green-600 dark:text-green-400': getSlugFeedback().type === 'success',
                            'text-red-500 dark:text-red-400': getSlugFeedback().type === 'error',
                            'text-blue-500 dark:text-blue-400': getSlugFeedback().type === 'info',
                        }"
                    >
                        {{ getSlugFeedback().text }}
                    </span>
                    <svg
                        v-if="slugChecking"
                        class="animate-spin h-3.5 w-3.5 text-blue-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                </div>
            </div>

            <DivDivider class="my-5" />

            <div class="space-y-4">
                <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Tema
                </p>

                <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-900 dark:text-gray-400">
                        Logo
                    </label>
                    <ToolbarFile
                        type="image"
                        title="Clique para selecionar ou enviar a logo"
                        :source="store.channel.more.theme.logo_url"
                        @selected="handleLogoSelected"
                    />
                    <p class="text-xs text-gray-400 dark:text-gray-400">
                        Imagem exibida na tela de login. Tamanho recomendado: 200x80 px.
                    </p>
                </div>

                <InputFormHorizontal
                    v-model="store.channel.more.theme.login_title"
                    :init-value="store.channel.more.theme.login_title"
                    label="Título do Login"
                    placeholder="Central de Atendimento"
                    rules="required"
                    vertical
                />

                <InputFormHorizontal
                    v-model="store.channel.more.theme.login_visitor_text"
                    :init-value="store.channel.more.theme.login_visitor_text"
                    label="Texto do botão visitante"
                    placeholder="Entrar como visitante"
                    vertical
                />

                <div class="input">
                    <label class="block text-sm font-medium text-gray-900 dark:text-gray-400 mb-1">
                        Descrição do Login
                    </label>
                    <textarea
                        v-model="store.channel.more.theme.login_description"
                        rows="3"
                        class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-colors resize-none"
                        placeholder="Breve descrição exibida na tela de login..."
                    />
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div class="input">
                        <label class="block text-sm font-medium text-gray-900 dark:text-gray-400 mb-1">
                            Cor Primária
                        </label>
                        <ColorInput
                            v-model="store.channel.more.theme.primary_color"
                            position="top right"
                            format="hex string"
                        />
                    </div>

                    <div class="input">
                        <label class="block text-sm font-medium text-gray-900 dark:text-gray-400 mb-1">
                            Cor Secundária
                        </label>
                        <ColorInput
                            v-model="store.channel.more.theme.secondary_color"
                            position="top right"
                            format="hex string"
                        />
                    </div>
                </div>
            </div>

            <DivDivider class="my-5" />

            <div class="space-y-4">
                <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Login / Autenticação
                </p>

                <InputCheckBoxWithDescription
                    :model-value="!!store.channel.more.login.auth_check"
                    label="Verificar autenticação"
                    description="Quando ativado, exige que o usuário esteja identificado via documento em alguma integração para iniciar o atendimento. 
                    Será usado as integrações cadastradas na sua conta para verificar a autenticação."
                    @update:model-value="handleAuthCheckChange"
                />

                <div class="input">
                    <label class="block text-sm font-medium text-gray-900 dark:text-gray-400 mb-1">
                        Tipo de Login
                    </label>
                    <select
                        v-model="store.channel.more.login.type"
                        class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-colors"
                    >
                        <option
                            v-for="opt in loginTypes"
                            :key="opt.value"
                            :value="opt.value"
                        >
                            {{ opt.label }}
                        </option>
                    </select>
                </div>

                <FeedbackAlert
                    v-if="store.channel.more.login.type === 'none'"
                    title="Apenas login de visitante"
                    content="Com o tipo de login definido como Nenhum, apenas o login de visitante ficará disponível."
                    type="warning"
                />

                <div class="input">
                    <label class="block text-sm font-medium text-gray-900 dark:text-gray-400 mb-1">
                        Campos Adicionais
                    </label>
                    <MultiSelect
                        v-model="additionalFields"
                        :options="additionalFieldsOptions"
                        option-label="label"
                        option-value="value"
                        display="chip"
                        placeholder="Selecione os campos"
                        class="w-full"
                    />
                </div>

                <div class="input">
                    <label class="block text-sm font-medium text-gray-900 dark:text-gray-400 mb-1">
                        País
                    </label>
                    <select
                        v-model="store.channel.more.login.country"
                        class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-colors"
                    >
                        <option
                            v-for="opt in countryOptions"
                            :key="opt.value"
                            :value="opt.value"
                        >
                            {{ opt.label }}
                        </option>
                    </select>
                </div>
            </div>

            <DivDivider class="my-5" />

            <div class="space-y-3">
                <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Features
                </p>

                <InputCheckBoxWithDescription
                    :model-value="!!store.channel.more.features.reboot_device"
                    label="Reinicializar dispositivo"
                    description="Permite reinicializar o dispositivo do cliente remotamente."
                    @update:model-value="store.channel.more.features.reboot_device = $event"
                />

                <InputCheckBoxWithDescription
                    :model-value="!!store.channel.more.features.change_wifi"
                    label="Alterar Wi-Fi"
                    description="Permite alterar a rede Wi-Fi do dispositivo remotamente."
                    @update:model-value="store.channel.more.features.change_wifi = $event"
                />

                <InputCheckBoxWithDescription
                    :model-value="!!store.channel.more.features.payment_promise"
                    label="Promessa de pagamento"
                    description="Habilita a funcionalidade de promessa de pagamento."
                    @update:model-value="store.channel.more.features.payment_promise = $event"
                />

                <InputCheckBoxWithDescription
                    :model-value="!!store.channel.more.features.duplicates"
                    label="Boletos"
                    description="Habilita a funcionalidade de boletos."
                    @update:model-value="store.channel.more.features.duplicates = $event"
                />

                <div v-if="!!store.channel.more.features.duplicates" class="space-y-3 pl-6">
                    <InputCheckBoxWithDescription
                        :model-value="!!store.channel.more.features.duplicates_options.omit_pix"
                        label="Omitir Pix"
                        description="Omite a opção de pagamento via Pix nos boletos."
                        @update:model-value="store.channel.more.features.duplicates_options.omit_pix = $event"
                    />

                    <InputCheckBoxWithDescription
                        :model-value="!!store.channel.more.features.duplicates_options.omit_barcode"
                        label="Omitir código de barras"
                        description="Omite o código de barras nos boletos."
                        @update:model-value="store.channel.more.features.duplicates_options.omit_barcode = $event"
                    />
                </div>
            </div>

            <DivDivider class="my-5" />

            <div class="space-y-4">
                <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Sugestões no Chat
                </p>

                <div
                    v-for="(suggestion, index) in store.channel.more.suggestions"
                    :key="index"
                    class="space-y-3 p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-neutral-800/50"
                >
                    <div class="flex items-center justify-between">
                        <span class="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Sugestão {{ index + 1 }}
                        </span>
                        <button
                            v-if="store.channel.more.suggestions.length > 1"
                            type="button"
                            class="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium transition-colors"
                            @click="removeSuggestion(index)"
                        >
                            Remover
                        </button>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div class="input">
                            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                Gatilho (trigger)
                            </label>
                            <input
                                v-model="suggestion.trigger"
                                type="text"
                                placeholder="Ex: Olá, Preciso de ajuda, Suporte"
                                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-colors"
                            />
                        </div>
                        <div class="input">
                            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                Descrição
                            </label>
                            <input
                                v-model="suggestion.description"
                                type="text"
                                placeholder="Ex: Como podemos ajudar?"
                                class="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 outline-none transition-colors"
                            />
                        </div>
                        <div class="input">
                            <label class="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                                Exibir para
                            </label>
                            <MultiSelect
                                v-model="suggestion.roles"
                                :options="suggestionRolesOptions"
                                option-label="label"
                                option-value="value"
                                display="chip"
                                placeholder="Selecione"
                                class="w-full"
                            />
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    class="w-full rounded-lg border border-dashed border-gray-300 dark:border-gray-600 px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400 hover:border-primary-400 hover:text-primary-500 dark:hover:border-primary-400 dark:hover:text-primary-400 transition-colors"
                    @click="addSuggestion"
                >
                    + Adicionar sugestão
                </button>
            </div>

            <DivDivider class="my-5" />

            <div class="space-y-4">
                <p class="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Notificações
                </p>

                <div class="space-y-2">
                    <label class="block text-sm font-medium text-gray-900 dark:text-gray-400">
                        Essa é a chave de acesso para enviar notificações para o contato diretamente pelo PWA.
                    </label>
                    <div class="flex items-center gap-2">
                        <input
                            :value="store.channel.more.notification_token"
                            type="text"
                            readonly
                            class="w-full min-w-0 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-neutral-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 font-mono focus:outline-none"
                        />
                        <button
                            type="button"
                            class="shrink-0 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:border-primary-400 hover:text-primary-500 dark:hover:border-primary-400 dark:hover:text-primary-400 transition-colors"
                            @click="copyNotificationToken"
                        >
                            Copiar
                        </button>
                        <button
                            type="button"
                            class="shrink-0 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:border-primary-400 hover:text-primary-500 dark:hover:border-primary-400 dark:hover:text-primary-400 transition-colors"
                            @click="rotateNotificationToken"
                        >
                            Rotacionar
                        </button>
                    </div>
                    <p class="text-xs text-gray-400 dark:text-gray-400">
                        Token usado no header <span class="font-mono">X-Channel-Token</span> para enviar notificações ao contato.
                    </p>
                    <button
                        type="button"
                        class="text-sm font-medium text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                        @click="showNotificationModal = true"
                    >
                        Ver documentação de envio
                    </button>
                </div>
            </div>

            <AlertPrice />

            <Dialog
                v-model:visible="showNotificationModal"
                modal
                header="Enviar notificação para um contato"
                :style="{ minWidth: '560px', maxWidth: '90vw' }"
            >
                <div class="space-y-5 text-sm text-gray-700 dark:text-gray-300">
                    <p>
                        Endpoint:
                        <code class="font-mono text-xs bg-gray-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded">
                            POST {{ apiDomain }}/api/channels/{{ channelIdForDoc }}/notifications
                        </code>
                    </p>

                    <div class="space-y-1">
                        <div class="flex items-center justify-between">
                            <span class="font-medium text-gray-900 dark:text-gray-100">Exemplo com cURL</span>
                            <button
                                type="button"
                                class="text-xs font-medium text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                                @click="copyCurl"
                            >
                                Copiar
                            </button>
                        </div>
                        <pre class="overflow-x-auto rounded-lg bg-gray-900 dark:bg-black p-4 text-xs text-gray-100 font-mono leading-relaxed whitespace-pre">{{ curlCommand }}</pre>
                    </div>

                    <div class="space-y-1">
                        <p class="font-medium text-gray-900 dark:text-gray-100">
                            Corpo (identifica o contato por um dos dois):
                        </p>
                        <table class="w-full text-xs">
                            <thead>
                                <tr class="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                                    <th class="py-2 pr-2 font-semibold">Campo</th>
                                    <th class="py-2 pr-2 font-semibold">Tipo</th>
                                    <th class="py-2 font-semibold">Obrigatório</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="border-b border-gray-100 dark:border-neutral-800">
                                    <td class="py-1.5 pr-2 font-mono">document</td>
                                    <td class="py-1.5 pr-2">string</td>
                                    <td class="py-1.5">sim* (ou external_client_id)</td>
                                </tr>
                                <tr class="border-b border-gray-100 dark:border-neutral-800">
                                    <td class="py-1.5 pr-2 font-mono">external_client_id</td>
                                    <td class="py-1.5 pr-2">string</td>
                                    <td class="py-1.5">sim* (ou document)</td>
                                </tr>
                                <tr class="border-b border-gray-100 dark:border-neutral-800">
                                    <td class="py-1.5 pr-2 font-mono">title</td>
                                    <td class="py-1.5 pr-2">string (máx 255)</td>
                                    <td class="py-1.5">sim</td>
                                </tr>
                                <tr class="border-b border-gray-100 dark:border-neutral-800">
                                    <td class="py-1.5 pr-2 font-mono">content</td>
                                    <td class="py-1.5 pr-2">string</td>
                                    <td class="py-1.5">sim</td>
                                </tr>
                                <tr class="border-b border-gray-100 dark:border-neutral-800">
                                    <td class="py-1.5 pr-2 font-mono">url</td>
                                    <td class="py-1.5 pr-2">string</td>
                                    <td class="py-1.5">não</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="space-y-1">
                        <p class="font-medium text-gray-900 dark:text-gray-100">Respostas:</p>
                        <ul class="list-disc list-inside space-y-1 text-xs">
                            <li><span class="font-mono">202 Accepted</span> — enfileirado para envio.</li>
                            <li><span class="font-mono">403</span> — token de canal ausente/incorreto ou canal sem token.</li>
                            <li><span class="font-mono">404</span> — canal não encontrado ou sem sessão ativa do contato.</li>
                            <li><span class="font-mono">422</span> — validação (faltou identificador/título/conteúdo).</li>
                        </ul>
                    </div>

                    <p class="text-xs text-gray-400 dark:text-gray-400">
                        A notificação chega ao PWA do contato com o payload
                        <span class="font-mono">{ title, body, icon, url }</span>, onde o icon é a logo_url do canal.
                    </p>
                </div>
            </Dialog>
        </div>
    </template>

    <template v-else-if="store.formStep === 1">
        <div class="space-y-4 text-sm text-gray-600 dark:text-gray-400">
            <p class="font-semibold text-gray-900 dark:text-gray-100">
                Instruções de cadastro — Webchat V2
            </p>
            <p>
                O Webchat V2 é a nova versão do canal de atendimento via web, com suporte aprimorado para autenticação de usuários, personalização completa de tema e integração via webhooks.
            </p>
            <ul class="list-disc list-inside space-y-1">
                <li>Configure o <strong>slug</strong> — ele será o identificador único do seu webchat na URL de acesso.</li>
                <li>Personalize as <strong>cores</strong>, <strong>logo</strong> e <strong>textos</strong> da tela de login.</li>
                <li>Defina o <strong>tipo de login</strong> e os campos que o usuário deverá preencher.</li>
            </ul>
        </div>
    </template>
</template>

<style scoped>
    :deep(.color-input .picker-popup) {
        z-index: 120000 !important;
    }
</style>
